import React, { useMemo, useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { sortElements } from '@BUP/utils'
import BupSpinner from '@BUP/components/Loading/Spinner'
import QuestionsList from '@BUP/components/Sociogram/QuestionsList'
import { useCreateQuestionResponse } from '@BUP/hooks/Sociogram/useCreateQuestionReponse'
import { useTranslation } from 'react-i18next'
import AlertComponent from '@BUP/components/Generics/Alert'
import { monitorException } from '@BUP/services/monitor'
import { enrichError } from '@BUP/services/monitor/enrichError'
import { useSendParticipantsTerminated } from '@BUP/hooks/Sociogram/participantsTerminated/useSendParticipantsTerminated'

const SurveyQuestionsList = ({ enrollmentId, survey, page, nextPage }) => {
  const [t] = useTranslation()
  const history = useHistory()
  const { id: surveyId, companions, courses } = survey

  const sortedCompanions = useMemo(() => {
    return Array.isArray(companions) ? sortElements(companions) : []
  }, [companions])

  const {
    mutateAsync: saveAllResponsesByPage,
    error: errorCreateResponse,
    isPending: loadingCreateQuestionResponse
  } = useCreateQuestionResponse(enrollmentId, surveyId)

  const {
    mutateAsync: sendEnrollmentId,
    isPending: loadingParticipantsTerminated,
    error: errorParticipantsTerminated
  } = useSendParticipantsTerminated()

  const userEnrollmentWithCourses = useMemo(() => {
    if (!Array.isArray(companions)) return null
    return companions.find(classmate => classmate.enrollment === enrollmentId)
  }, [companions, enrollmentId])

  const handleFormSubmit = useCallback(
    async (responses) => {
      try {
        await saveAllResponsesByPage(responses)
        if (page === 6) {
          try {
            await sendEnrollmentId({ data: { enrollmentId, survey: surveyId } })
            history.push('/sociogram/start-survey')
          } catch (err) {
            monitorException(new Error('Error sending participant terminated', { cause: err }))
          }
        } else {
          nextPage()
        }
      } catch (err) {
        const error = new Error('Error sending sociogram responses', { cause: err })
        const enrichedError = enrichError(err, { enrollmentId, surveyId })

        monitorException(error, {
          tags: { location: 'SurveyQuestionsList', page },
          extra: enrichedError
        })
      }
    },
    [saveAllResponsesByPage, sendEnrollmentId, page, enrollmentId, surveyId, history, nextPage]
  )

  if (loadingCreateQuestionResponse || loadingParticipantsTerminated) {
    return (
      <BupSpinner
        size='large'
        tip={t('pages.data.answerSociogram.spinnerText')}
      />
    )
  }

  const ErrorMessage = () => {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '500px' }}>
        <AlertComponent
          type='info'
          message={t('pages.data.answerSociogram.noDataError')}
        />
      </div>
    )
  }

  if (!sortedCompanions || sortedCompanions.length === 0) {
    monitorException({ extra: `Error en CurrentSociogram, surveyId: ${surveyId}` })
    return <ErrorMessage />
  }

  const userCourses = userEnrollmentWithCourses?.courses ?? []
  const groupCoursesData = courses

  return (
    <QuestionsList
      classMates={sortedCompanions}
      onSubmit={handleFormSubmit}
      courses={userCourses}
      groupCoursesData={groupCoursesData}
      errorCreateResponse={errorCreateResponse}
      errorParticipantsTerminated={errorParticipantsTerminated}
      page={page}
      surveyId={surveyId}
    />
  )
}

export default SurveyQuestionsList
